import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inquiryOptions = [
  'Healthcare System',
  'Government / Municipal Agency',
  'Community Organization',
  'Strategic Partnership',
  'Other',
];

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    inquiry_type: '',
    consulting_needs: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.organization || !formData.email || !formData.consulting_needs) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Inquiry received. We will be in touch shortly.');
      setFormData({
        name: '',
        organization: '',
        email: '',
        inquiry_type: '',
        consulting_needs: '',
      });
    } catch (err) {
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldLabel = 'text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] font-medium';
  const inputBase =
    'h-12 vs-input rounded-sm px-4 text-sm text-white placeholder:text-slate-500';

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="name" className={fieldLabel}>
            Full Name <span className="text-[#D4AF37]">*</span>
          </Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            data-testid="contact-name-input"
            placeholder="Your full name"
            className={inputBase}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="organization" className={fieldLabel}>
            Organization / Agency <span className="text-[#D4AF37]">*</span>
          </Label>
          <Input
            id="organization"
            name="organization"
            value={formData.organization}
            onChange={handleChange}
            data-testid="contact-organization-input"
            placeholder="Hospital, agency, city, or organization"
            className={inputBase}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="email" className={fieldLabel}>
            Email Address <span className="text-[#D4AF37]">*</span>
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            data-testid="contact-email-input"
            placeholder="your@email.com"
            className={inputBase}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="inquiry_type" className={fieldLabel}>
            Nature of Inquiry
          </Label>
          <Select
            value={formData.inquiry_type}
            onValueChange={(v) => setFormData((p) => ({ ...p, inquiry_type: v }))}
          >
            <SelectTrigger
              id="inquiry_type"
              data-testid="contact-inquiry-type-trigger"
              className="h-12 vs-input rounded-sm text-sm text-white data-[placeholder]:text-slate-500"
            >
              <SelectValue placeholder="Select inquiry type" />
            </SelectTrigger>
            <SelectContent className="bg-[#221141] text-white border-white/10">
              {inquiryOptions.map((opt) => (
                <SelectItem
                  key={opt}
                  value={opt}
                  data-testid={`inquiry-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className="focus:bg-[#D4AF37] focus:text-[#1B0E33]"
                >
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="consulting_needs" className={fieldLabel}>
          Message <span className="text-[#D4AF37]">*</span>
        </Label>
        <Textarea
          id="consulting_needs"
          name="consulting_needs"
          value={formData.consulting_needs}
          onChange={handleChange}
          data-testid="contact-message-textarea"
          placeholder="Tell us about your organization, your priorities, and how Vantaris can support you."
          className="min-h-[160px] vs-input rounded-sm p-4 text-sm text-white placeholder:text-slate-500 resize-none"
        />
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        data-testid="contact-submit-btn"
        className="w-full md:w-auto bg-[#D4AF37] text-[#1B0E33] hover:bg-[#E5C75B] px-8 py-4 h-auto rounded-sm text-[12px] font-semibold uppercase tracking-[0.12em] transition-all"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            Send Inquiry
            <ArrowRight className="w-4 h-4 ml-2" />
          </>
        )}
      </Button>
    </form>
  );
};

export default ContactForm;
